import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { SeatsioSeatingChart } from '@seatsio/seatsio-react';
import type { EventManager, SeatingChart } from '@seatsio/seatsio-types';

import useGetPartnerData from 'features/common/hooks/useGetPartnerData';
import type { SeatsSelectionChartProps } from 'features/common/hooks/useSeatsSelection';

import styles from './PunchupBuyerSeatsWidget.module.css';
import { SEATS_COLORS, SEATS_STYLE, toSeatsLanguage, toSeatsRegion } from '../PunchupSeatsWidget/seatsTheme';

interface PunchupBuyerSeatsWidgetProps {
	eventKey: string | null | undefined;
	chartProps?: SeatsSelectionChartProps;
	/** Cap the number of seats the buyer can select on the chart (seats.io `maxSelectedObjects`).
	 *  Omit for no cap. The Switch flow passes 1 so a single-seat reissue can't over-select. */
	maxSelectedObjects?: number;
	/** Per-category prices shown on the chart and tooltip. */
	pricing?: Array<{ category: string; price: number }>;
	/** Formats the price shown on the chart (e.g. $25.00). */
	priceFormatter?: (price: number) => string;
	/** seats.io tooltip behaviour — e.g. hide the category line when we render our own. */
	objectTooltip?: { showCategory?: boolean };
	/** Tooltip text override — return the per-object label shown next to the price. */
	tooltipInfo?: (object: { category?: { key?: string | number } }) => string;
	/** seats.io session mode — `'start'` for box-office, default `'continue'` for buyer reentry. */
	session?: 'start' | 'continue' | 'manual' | 'none';
	/** seats.io channel keys to unlock — seats reserved for these channels become selectable.
	 *  Omit (or pass undefined) to show only generally-available seats. */
	channels?: string[];
	/** Override the session partner's PUBLIC workspace key — for super-admin tooling
	 *  (e.g. Seats Sync reassignment) that renders another partner's chart. */
	workspaceKey?: string;
	/** Override the session partner's seats region; only meaningful with `workspaceKey`. */
	seatsRegion?: string;
}

export const PunchupBuyerSeatsWidget: FC<PunchupBuyerSeatsWidgetProps> = ({
	eventKey,
	chartProps,
	maxSelectedObjects,
	pricing,
	priceFormatter,
	objectTooltip,
	tooltipInfo,
	session,
	channels,
	workspaceKey,
	seatsRegion,
}) => {
	const { i18n } = useTranslation();
	const { partnerData } = useGetPartnerData();

	const workspace = workspaceKey ?? partnerData?.seats_io_workspace_key;
	if (!eventKey || !workspace) {
		return null;
	}

	const region = toSeatsRegion(seatsRegion ?? partnerData?.seats_region);

	// SeatingChart and EventManager both expose deselectObjects — the only method the hook uses.
	const { onChartRendered: hookOnChartRendered, ...restChartProps } = chartProps ?? {};
	const bridgedOnChartRendered = hookOnChartRendered
		? (chart: SeatingChart) => hookOnChartRendered(chart as unknown as EventManager)
		: undefined;

	return (
		<div className={styles.container}>
			<SeatsioSeatingChart
				workspaceKey={workspace}
				event={eventKey}
				region={region}
				colorScheme="light"
				language={toSeatsLanguage(i18n.language)}
				colors={SEATS_COLORS}
				style={SEATS_STYLE}
				maxSelectedObjects={maxSelectedObjects}
				pricing={pricing}
				priceFormatter={priceFormatter}
				objectTooltip={objectTooltip}
				tooltipInfo={tooltipInfo}
				session={session}
				channels={channels}
				{...restChartProps}
				{...(bridgedOnChartRendered ? { onChartRendered: bridgedOnChartRendered } : undefined)}
			/>
		</div>
	);
};
